/**
 * Merchant category observation helpers (docs/03 §2 Stage 0; Task 21).
 * ≥2 consistent implied_category observations for merchant+channel → observed.
 */

import type { CategorySource, SpendCategory, TxnChannel } from './types';

export type CategoryObservationInput = {
  merchant_id: string;
  channel: TxnChannel;
  /** User-corrected / implied category from an observation row. */
  implied_category: SpendCategory | null;
};

/**
 * Auto-classified = engine chose the category (not an explicit user override on the query).
 * Corrections are offered only for these sources (Task 21 AC).
 */
export function isAutoClassifiedCategory(source: CategorySource): boolean {
  return source !== 'user';
}

/**
 * docs/03 §2 authority #2: ≥2 consistent implied_category for merchant+channel.
 * Returns null when the observation threshold is not met.
 */
export function categoryFromObservations(
  merchantId: string,
  channel: TxnChannel,
  observations: readonly CategoryObservationInput[],
): SpendCategory | null {
  const counts = new Map<SpendCategory, number>();
  for (const o of observations) {
    if (o.merchant_id !== merchantId) continue;
    if (o.channel !== channel) continue;
    if (o.implied_category === null) continue;
    counts.set(o.implied_category, (counts.get(o.implied_category) ?? 0) + 1);
  }

  let best: SpendCategory | null = null;
  let bestCount = 0;
  for (const [cat, n] of counts) {
    if (n < 2) continue;
    if (best === null || n > bestCount || (n === bestCount && cat < best)) {
      best = cat;
      bestCount = n;
    }
  }
  return best;
}
